import React, { useMemo, useRef, useState } from "react";
import "./MBHazardsTab.css";

export default function MBHazardsTab({
  hazardLayers = [],
  activeHazardLayerId = "",
  snapshots = [],
  activeSnapshotId = "",
  selectedSnapshotRows = [],
  selectedGeoJson = null,
  selectedFeatureIndex = "",
  isBusy = false,
  error = "",
  success = "",
  onSelectHazardLayer,
  onCreateHazardLayer,
  onDeleteHazardLayer,
  onUploadSnapshots,
  onSelectSnapshot,
  onDeleteSnapshot,
  onSelectFeature,
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [newLayerName, setNewLayerName] = useState("");
  const fileInputRef = useRef(null);
  const orderedSnapshots = useMemo(
    () => [...snapshots].sort((a, b) => Number(a.timeMin) - Number(b.timeMin)),
    [snapshots]
  );
  const activeSnapshot = orderedSnapshots.find((row) => row.id === activeSnapshotId) || null;
  const selectedFeatures = useMemo(() => {
    const seen = new Set();
    return selectedSnapshotRows.filter((row, idx) => {
      const key = String(row?.feature_index || idx + 1);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [selectedSnapshotRows]);
  const selectedFeature = selectedFeatures.find((row) =>
    String(row.feature_index) === String(selectedFeatureIndex)
  ) || null;
  const minTime = orderedSnapshots.length ? Number(orderedSnapshots[0].timeMin) : 0;
  const maxTime = orderedSnapshots.length
    ? Number(orderedSnapshots[orderedSnapshots.length - 1].timeMin)
    : 0;

  const selectNearestTime = (raw) => {
    if (!orderedSnapshots.length) return;
    const wanted = Number(raw);
    const nearest = orderedSnapshots.reduce((best, row) =>
      Math.abs(Number(row.timeMin) - wanted) < Math.abs(Number(best.timeMin) - wanted) ? row : best
    );
    onSelectSnapshot?.(nearest.id);
  };

  const createLayer = async () => {
    const ok = await onCreateHazardLayer?.(newLayerName);
    if (!ok) return;
    setNewLayerName("");
    setIsCreating(false);
  };

  const downloadSelectedGeoJson = () => {
    if (!selectedGeoJson || !activeSnapshot) return;
    const blob = new Blob([JSON.stringify(selectedGeoJson, null, 2)], { type: "application/geo+json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = activeSnapshot.originalFilename || activeSnapshot.storageFilename || "hazard.geojson";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mb-hazard-shell">
      <div className="mb-hazard-header">
        <div className="mb-hazard-heading">Hazard Layers</div>
        <button className="mb-btn mb-btnSecondary" type="button" disabled={isBusy} onClick={() => setIsCreating(true)}>
          Create
        </button>
      </div>

      {isCreating ? (
        <div className="mb-hazard-create">
          <label className="mb-hazard-label">Hazard layer name</label>
          <input className="mb-hazard-input" value={newLayerName} onChange={(e) => setNewLayerName(e.target.value)} disabled={isBusy} />
          <div className="mb-hazard-actions">
            <button className="mb-btn mb-btnSecondary" type="button" disabled={isBusy} onClick={() => setIsCreating(false)}>Cancel</button>
            <button className="mb-btn" type="button" disabled={isBusy || !newLayerName.trim()} onClick={createLayer}>Save</button>
          </div>
        </div>
      ) : null}

      <div className="mb-hazard-field">
        <label className="mb-hazard-label">Hazard Layer</label>
        <select className="mb-hazard-select" value={activeHazardLayerId} disabled={isBusy || !hazardLayers.length} onChange={(e) => onSelectHazardLayer?.(e.target.value)}>
          {!hazardLayers.length ? <option value="">No hazard layers</option> : null}
          {hazardLayers.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        </select>
      </div>

      {activeHazardLayerId ? (
        <>
          <div className="mb-hazard-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              multiple
              hidden
              onChange={(e) => {
                const files = [...(e.target.files || [])];
                e.target.value = "";
                if (files.length) onUploadSnapshots?.(files);
              }}
            />
            <button className="mb-btn" type="button" disabled={isBusy} onClick={() => fileInputRef.current?.click()}>Upload GeoJSON snapshots</button>
            <button className="mb-btn mb-btnDanger" type="button" disabled={isBusy} onClick={onDeleteHazardLayer}>Delete layer</button>
          </div>

          <div className="mb-hazard-field">
            <label className="mb-hazard-label">Time: {activeSnapshot ? `${activeSnapshot.timeMin} min` : "No snapshots"}</label>
            <input
              className="mb-hazard-range"
              type="range"
              min={minTime}
              max={maxTime}
              step="any"
              value={activeSnapshot ? activeSnapshot.timeMin : minTime}
              disabled={isBusy || !orderedSnapshots.length || minTime === maxTime}
              onChange={(e) => selectNearestTime(e.target.value)}
            />
            <div className="mb-hazard-rangeLabels"><span>{minTime} min</span><span>{maxTime} min</span></div>
          </div>

          <div className="mb-hazard-field">
            <label className="mb-hazard-label">Uploaded GeoJSON snapshots</label>
            <div className="mb-hazard-list">
              {orderedSnapshots.map((row) => (
                <div key={row.id} className={`mb-hazard-row ${row.id === activeSnapshotId ? "isActive" : ""}`}>
                  <button type="button" className="mb-hazard-rowSelect" onClick={() => onSelectSnapshot?.(row.id)}>
                    <span>{row.originalFilename || row.name}</span><strong>{row.timeMin} min</strong>
                  </button>
                  <button type="button" className="mb-hazard-delete" disabled={isBusy} onClick={() => onDeleteSnapshot?.(row.id)}>Delete</button>
                </div>
              ))}
              {!orderedSnapshots.length ? <div className="mb-hazard-empty">No snapshots uploaded.</div> : null}
            </div>
          </div>

          {activeSnapshot ? (
            <div className="mb-hazard-field">
              <div className="mb-hazard-selectedHeader">
                <label className="mb-hazard-label">Selected GeoJSON properties</label>
                <button className="mb-btn mb-btnSecondary" type="button" disabled={!selectedGeoJson} onClick={downloadSelectedGeoJson}>
                  Download GeoJSON
                </button>
              </div>
              <div className="mb-hazard-properties">
                <div className="mb-hazard-property"><span>filename</span><strong>{activeSnapshot.originalFilename}</strong></div>
                <div className="mb-hazard-property"><span>time_min</span><strong>{activeSnapshot.timeMin}</strong></div>
                <div className="mb-hazard-property"><span>feature_count</span><strong>{activeSnapshot.featureCount}</strong></div>
              </div>
              <label className="mb-hazard-label">Features</label>
              <div className="mb-hazard-featureList">
                <button
                  type="button"
                  className={`mb-hazard-featureSelect ${selectedFeatureIndex === "" ? "isActive" : ""}`}
                  onClick={() => onSelectFeature?.("")}
                >
                  <span>All features</span><strong>{selectedFeatures.length}</strong>
                </button>
                {selectedFeatures.map((row, idx) => (
                  <button
                    type="button"
                    key={row.feature_index || idx + 1}
                    className={`mb-hazard-featureSelect ${String(row.feature_index || idx + 1) === String(selectedFeatureIndex) ? "isActive" : ""}`}
                    onClick={() => onSelectFeature?.(row.feature_index || idx + 1)}
                  >
                    Feature {row.feature_index || idx + 1}
                  </button>
                ))}
              </div>
              {selectedFeature ? (
                <div className="mb-hazard-properties">
                  <div className="mb-hazard-featureTitle">Feature {selectedFeature.feature_index}</div>
                  {Object.entries(selectedFeature.properties || {}).map(([key, value]) => (
                    <div className="mb-hazard-property" key={`${selectedFeature.polygon_id}-${key}`}>
                      <span>{key}</span><strong>{typeof value === "object" ? JSON.stringify(value) : String(value)}</strong>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {error ? <div className="mb-hazard-msg isError">{error}</div> : null}
      {success ? <div className="mb-hazard-msg isOk">{success}</div> : null}
    </div>
  );
}
